
import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, Send, Archive, RotateCcw, Settings2, ChevronLeft, Save, Mail, Check, ListTodo } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import RichTextEditor from '@/components/RichTextEditor';
import RichTextDisplay from '@/components/RichTextDisplay';
import { Checkbox } from '@/components/ui/checkbox';

const STATUS_OPTIONS = ['New Lead', 'Outreach Sent', 'Proposal Out', 'Follow-Up', 'Won', 'Lost'];
const STATUS_COLORS = {
  'New Lead': 'bg-slate-500/20 text-slate-300 border-slate-500/30',
  'Outreach Sent': 'bg-blue-500/20 text-blue-300 border-blue-500/30',
  'Proposal Out': 'bg-amber-500/20 text-amber-300 border-amber-500/30',
  'Follow-Up': 'bg-purple-500/20 text-purple-300 border-purple-500/30',
  'Won': 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30',
  'Lost': 'bg-rose-500/20 text-rose-300 border-rose-500/30',
};

export default function PipelineSummaryTab({ users = [] }) {
  const [board, setBoard] = useState(null);
  const [archive, setArchive] = useState(null);
  const [viewMode, setViewMode] = useState('current');
  const [addPickerOpen, setAddPickerOpen] = useState(false);
  const [recipsOpen, setRecipsOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [actionItemForm, setActionItemForm] = useState({ action: '', owner_id: '', due_date: '', create_todo: false });
  const [needsTeamDraft, setNeedsTeamDraft] = useState('');
  const [needsTeamDirty, setNeedsTeamDirty] = useState(false);
  // Auto-save state for Needs the Team: 'idle' | 'saving' | 'saved' | 'error'
  const [needsTeamSaveStatus, setNeedsTeamSaveStatus] = useState('idle');
  const needsTeamTimer = useRef(null);
  const needsTeamSavedTimer = useRef(null);
  // Cleanup pending timers on unmount so we don't leak fires
  useEffect(() => () => {
    clearTimeout(needsTeamTimer.current);
    clearTimeout(needsTeamSavedTimer.current);
  }, []);

  const load = useCallback(async () => {
    try {
      const [cr, ar] = await Promise.all([
        api.get('/pipeline-board'),
        api.get('/pipeline-board/archive'),
      ]);
      setBoard(cr.data);
      setArchive(ar.data);
      setNeedsTeamDraft(cr.data?.needs_team || '');
      setNeedsTeamDirty(false);
      setNeedsTeamSaveStatus('idle');
    } catch (e) {
      toast.error('Failed to load pipeline board');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const displayBoard = viewMode === 'current' ? board : archive;
  const readOnly = viewMode === 'archive';

  // Auto-save Needs the Team after the user pauses typing for 800ms.
  // Same proven debounce pattern used by InvoiceNotesEditor.
  const handleNeedsTeamChange = useCallback((v) => {
    setNeedsTeamDraft(v);
    setNeedsTeamDirty(true);
    setNeedsTeamSaveStatus('saving');
    clearTimeout(needsTeamTimer.current);
    needsTeamTimer.current = setTimeout(async () => {
      try {
        await api.put('/pipeline-board/needs-team', { needs_team: v });
        setNeedsTeamDirty(false);
        setNeedsTeamSaveStatus('saved');
        // Fade the "Saved" pill back to idle after 2s so the UI stays calm
        clearTimeout(needsTeamSavedTimer.current);
        needsTeamSavedTimer.current = setTimeout(() => setNeedsTeamSaveStatus('idle'), 2000);
      } catch {
        setNeedsTeamSaveStatus('error');
      }
    }, 800);
  }, []);

  // Manual save fallback (button still present in case of error state)
  const saveNeedsTeam = async () => {
    try {
      clearTimeout(needsTeamTimer.current);
      setNeedsTeamSaveStatus('saving');
      await api.put('/pipeline-board/needs-team', { needs_team: needsTeamDraft });
      setNeedsTeamDirty(false);
      setNeedsTeamSaveStatus('saved');
      clearTimeout(needsTeamSavedTimer.current);
      needsTeamSavedTimer.current = setTimeout(() => setNeedsTeamSaveStatus('idle'), 2000);
      toast.success('Saved');
    } catch {
      setNeedsTeamSaveStatus('error');
      toast.error('Error saving');
    }
  };

  const clearBoard = async () => {
    try {
      await api.post('/pipeline-board/clear');
      setConfirmClear(false);
      toast.success('Board cleared. Previous content archived');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error clearing board');
    }
  };

  const sendDigest = async (source = viewMode) => {
    try {
      const r = await api.post('/pipeline-board/send-digest', { source });
      toast.success(`Digest queued to ${r.data.recipient_count} recipient(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to send digest');
    }
  };

  if (!board) return <p className="text-xs text-muted-foreground text-center py-8">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="pipeline-summary-tab">
      {/* Header Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-1 bg-card border border-border rounded-md p-0.5">
          <button
            className={`px-3 py-1 text-xs rounded ${viewMode === 'current' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
            onClick={() => setViewMode('current')}
            data-testid="pipeline-view-current"
          >Current</button>
          <button
            className={`px-3 py-1 text-xs rounded ${viewMode === 'archive' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'} ${!archive ? 'opacity-50' : ''}`}
            onClick={() => archive && setViewMode('archive')}
            disabled={!archive}
            data-testid="pipeline-view-archive"
          >{archive ? <><Archive className="h-3 w-3 mr-1 inline" />Previous Board</> : 'No Archive'}</button>
        </div>
        {viewMode === 'current' && (
          <Button size="sm" variant="outline" className="text-xs h-8 border-border" onClick={() => setAddPickerOpen(true)} data-testid="pipeline-add-row-btn">
            <Plus className="h-3 w-3 mr-1" /> Add Lead/RFP
          </Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <Popover open={recipsOpen} onOpenChange={setRecipsOpen}>
            <PopoverTrigger asChild>
              <Button size="sm" variant="outline" className="text-xs h-8 border-border" data-testid="pipeline-recipients-btn">
                <Settings2 className="h-3 w-3 mr-1" /> Recipients ({(board.digest_recipients || []).length})
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 bg-card border-border p-3">
              <RecipientManager recipients={board.digest_recipients || []} onSaved={() => { load(); setRecipsOpen(false); }} />
            </PopoverContent>
          </Popover>
          <Button size="sm" className="text-xs h-8 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => sendDigest()} data-testid="pipeline-send-digest-btn">
            <Send className="h-3 w-3 mr-1" /> Send Digest
          </Button>
          {viewMode === 'current' && (
            <Button size="sm" variant="outline" className="text-xs h-8 border-rose-500/40 text-rose-400 hover:bg-rose-500/10" onClick={() => setConfirmClear(true)} data-testid="pipeline-clear-btn">
              <RotateCcw className="h-3 w-3 mr-1" /> Clear Board
            </Button>
          )}
        </div>
      </div>

      {viewMode === 'archive' && archive && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ChevronLeft className="h-3 w-3" />
          Viewing archived board from <span className="font-medium text-foreground">{(archive.cleared_at || '').slice(0, 10)}</span>
          <Button size="sm" variant="ghost" className="ml-auto text-xs h-7" onClick={() => setViewMode('current')}>Back to current</Button>
        </div>
      )}

      {/* Status Legend */}
      <div className="flex items-center gap-2 flex-wrap text-xs">
        <span className="text-muted-foreground">Legend:</span>
        {STATUS_OPTIONS.map(s => (
          <Badge key={s} className={`${STATUS_COLORS[s]} border text-xs`}>{s}</Badge>
        ))}
      </div>

      {/* Pipeline This Week */}
      <Card className="bg-card border-border" data-testid="pipeline-table-section">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">Pipeline This Week</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="p-2 text-left font-semibold">Lead / RFP</th>
                  <th className="p-2 text-left font-semibold">Status</th>
                  <th className="p-2 text-left font-semibold">Next Step</th>
                  <th className="p-2 text-left font-semibold">Owner</th>
                  <th className="p-2 text-left font-semibold">Due</th>
                  {!readOnly && <th className="p-2 w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {(displayBoard?.pipeline_rows || []).length === 0 ? (
                  <tr><td colSpan={6} className="p-6 text-center text-muted-foreground">No entries. Click <span className="text-primary">Add Lead/RFP</span> above.</td></tr>
                ) : (
                  displayBoard.pipeline_rows.map(row => (
                    <PipelineRow key={row.id} row={row} users={users} readOnly={readOnly} onUpdate={load} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Action Items — moved above Additional Notes so the actionable
          items surface first when the team scans the summary. */}
      <Card className="bg-card border-border" data-testid="pipeline-action-items-section">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold mb-3">Action Items: Leaving the Call</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th className="p-2 text-left font-semibold w-8"></th>
                  <th className="p-2 text-left font-semibold">Action</th>
                  <th className="p-2 text-left font-semibold w-40">Owner</th>
                  <th className="p-2 text-left font-semibold w-32">Due</th>
                  {!readOnly && <th className="p-2 w-8"></th>}
                </tr>
              </thead>
              <tbody>
                {(displayBoard?.action_items || []).length === 0 ? (
                  <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No action items.</td></tr>
                ) : (
                  displayBoard.action_items.map(item => (
                    <ActionItemRow key={item.id} item={item} users={users} readOnly={readOnly} onUpdate={load} />
                  ))
                )}
                {!readOnly && (
                  <tr className="border-t border-border">
                    <td className="p-2"></td>
                    <td className="p-2">
                      <Input
                        placeholder="New action…"
                        value={actionItemForm.action}
                        onChange={e => setActionItemForm({ ...actionItemForm, action: e.target.value })}
                        className="bg-background border-border text-xs h-8"
                        data-testid="pipeline-new-action-input"
                      />
                    </td>
                    <td className="p-2">
                      <Select value={actionItemForm.owner_id || 'none'} onValueChange={v => setActionItemForm({ ...actionItemForm, owner_id: v === 'none' ? '' : v })}>
                        <SelectTrigger className="bg-background border-border text-xs h-8">
                          <SelectValue placeholder="Owner" />
                        </SelectTrigger>
                        <SelectContent className="bg-card border-border max-h-60">
                          <SelectItem value="none" className="text-xs">Unassigned</SelectItem>
                          {users.filter(u => u.role !== 'Client').map(u => (
                            <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="p-2">
                      <Input type="date" value={actionItemForm.due_date} onChange={e => setActionItemForm({ ...actionItemForm, due_date: e.target.value })} className="bg-background border-border text-xs h-8" />
                    </td>
                    <td className="p-2">
                      <Button size="icon" className="h-7 w-7 bg-primary text-primary-foreground" onClick={async () => {
                        if (!actionItemForm.action.trim()) return toast.error('Action text required');
                        if (actionItemForm.create_todo && !actionItemForm.owner_id) {
                          return toast.error('Pick an assignee to create a To-Do');
                        }
                        try {
                          await api.post('/pipeline-board/action-items', {
                            action: actionItemForm.action,
                            owner_id: actionItemForm.owner_id || null,
                            due_date: actionItemForm.due_date || null,
                            create_todo: actionItemForm.create_todo,
                          });
                          if (actionItemForm.create_todo) {
                            toast.success('Action item added + To-Do assigned');
                          }
                          setActionItemForm({ action: '', owner_id: '', due_date: '', create_todo: false });
                          load();
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'Error adding action');
                        }
                      }} data-testid="pipeline-add-action-btn">
                        <Plus className="h-3 w-3" />
                      </Button>
                    </td>
                  </tr>
                )}
                {!readOnly && (
                  <tr>
                    <td colSpan={5} className="px-2 pb-2 pt-0">
                      <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none" data-testid="pipeline-create-todo-label">
                        <Checkbox
                          checked={actionItemForm.create_todo}
                          onCheckedChange={(v) => setActionItemForm({ ...actionItemForm, create_todo: !!v })}
                          data-testid="pipeline-create-todo-checkbox"
                        />
                        <ListTodo className="h-3 w-3" />
                        Also create a To-Do for the assignee
                        {actionItemForm.create_todo && !actionItemForm.owner_id && (
                          <span className="text-amber-400">(pick an owner first)</span>
                        )}
                      </label>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Notes (renamed from "Needs the Team" - persisted field
          stays `needs_team` in the DB to avoid a migration). */}
      <Card className="bg-card border-border" data-testid="pipeline-needs-team-section">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-semibold">Additional Notes</h3>
            {!readOnly && (
              <div className="flex items-center gap-2">
                {needsTeamSaveStatus === 'saving' && (
                  <span className="text-xs text-muted-foreground italic flex items-center gap-1" data-testid="pipeline-needs-team-saving">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-400 animate-pulse" />
                    Saving...
                  </span>
                )}
                {needsTeamSaveStatus === 'saved' && (
                  <span className="text-xs text-emerald-400 flex items-center gap-1" data-testid="pipeline-needs-team-saved">
                    <Check className="h-3 w-3" /> Saved
                  </span>
                )}
                {needsTeamSaveStatus === 'error' && (
                  <Button size="sm" variant="outline" className="text-xs h-7 border-rose-500/40 text-rose-300" onClick={saveNeedsTeam} data-testid="pipeline-retry-save-needs-team">
                    <Save className="h-3 w-3 mr-1" /> Retry Save
                  </Button>
                )}
              </div>
            )}
          </div>
          {readOnly ? (
            <RichTextDisplay value={displayBoard?.needs_team || '<em>None</em>'} collapsedHeight={100} />
          ) : (
            <RichTextEditor
              value={needsTeamDraft}
              onChange={handleNeedsTeamChange}
              placeholder="Extra context, escalations, approvals needed, follow-ups..."
              data-testid="pipeline-needs-team-editor"
            />
          )}
          {!readOnly && (
            <p className="text-xs text-muted-foreground italic mt-1.5">Auto-saves as you type.</p>
          )}
        </CardContent>
      </Card>

      {/* Add Lead/RFP Modal */}
      {addPickerOpen && (
        <AddSourceModal
          open={addPickerOpen}
          onOpenChange={setAddPickerOpen}
          onAdded={load}
          existingRows={board?.pipeline_rows || []}
        />
      )}

      {/* Confirm Clear Modal */}
      <Dialog open={confirmClear} onOpenChange={setConfirmClear}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Clear Board</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will archive the current board (overwriting any previous archive) and start fresh.
            You&apos;ll still be able to send a digest of the archived content from the Previous Board view.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmClear(false)}>Cancel</Button>
            <Button className="bg-rose-600 hover:bg-rose-700 text-white" onClick={clearBoard} data-testid="pipeline-clear-confirm-btn">
              Clear & Archive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// -- Subcomponents --

function PipelineRow({ row, users, readOnly, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    status: row.override_status || row.live_status || '',
    next_step: row.override_next_step || row.live_next_step || '',
    owner_id: row.override_owner_id || '',
    due_date: row.override_due_date || row.live_due_date || '',
  });

  const ownerDisplay = row.override_owner_id
    ? (users.find(u => u.id === row.override_owner_id)?.name || '-')
    : (row.live_owner_name || '-');
  const status = row.override_status || row.live_status || '';
  const nextStep = row.override_next_step || row.live_next_step || '';
  const due = row.override_due_date || row.live_due_date || '';

  const save = async (updateSource) => {
    try {
      await api.put(`/pipeline-board/pipeline-rows/${row.id}`, {
        status: form.status,
        next_step: form.next_step,
        owner_id: form.owner_id || null,
        due_date: form.due_date || null,
        update_source: updateSource,
      });
      toast.success(updateSource ? 'Saved & pushed to source record' : 'Saved on board');
      setEditing(false);
      onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const del = async () => {
    if (!window.confirm('Remove this row from the board?')) return;
    try {
      await api.delete(`/pipeline-board/pipeline-rows/${row.id}`);
      onUpdate();
    } catch { toast.error('Delete failed'); }
  };

  if (editing && !readOnly) {
    return (
      <tr className="border-b border-border bg-muted/10" data-testid={`pipeline-row-${row.id}-edit`}>
        <td className="p-2">
          <Badge variant="outline" className="text-xs mr-1">{row.source_type === 'lead' ? 'Lead' : 'RFP'}</Badge>
          {row.label || row.source_id.slice(0, 8)}
        </td>
        <td className="p-2">
          <Select value={form.status || 'none'} onValueChange={v => setForm({ ...form, status: v === 'none' ? '' : v })}>
            <SelectTrigger className="bg-background border-border text-xs h-8"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card border-border">
              <SelectItem value="none" className="text-xs text-muted-foreground">-</SelectItem>
              {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s} className="text-xs">{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="p-2">
          <Input value={form.next_step} onChange={e => setForm({ ...form, next_step: e.target.value })} className="bg-background border-border text-xs h-8" />
        </td>
        <td className="p-2">
          <Select value={form.owner_id || 'none'} onValueChange={v => setForm({ ...form, owner_id: v === 'none' ? '' : v })}>
            <SelectTrigger className="bg-background border-border text-xs h-8"><SelectValue placeholder="-" /></SelectTrigger>
            <SelectContent className="bg-card border-border max-h-60">
              <SelectItem value="none" className="text-xs">Unassigned</SelectItem>
              {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="p-2">
          <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border text-xs h-8" />
        </td>
        <td className="p-2">
          <div className="flex gap-0.5">
            <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-500" onClick={() => save(false)} title="Save on board only" data-testid={`save-row-${row.id}-board-only`}>
              <Save className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-blue-500" onClick={() => save(true)} title="Save + push to source record" data-testid={`save-row-${row.id}-push`}>
              <Mail className="h-3 w-3" />
            </Button>
            <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditing(false)}>✕</Button>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr className="border-b border-border hover:bg-muted/10 cursor-pointer" onClick={() => !readOnly && setEditing(true)} data-testid={`pipeline-row-${row.id}`}>
      <td className="p-2">
        <Badge variant="outline" className="text-xs mr-1">{row.source_type === 'lead' ? 'Lead' : 'RFP'}</Badge>
        <span className={row.source_deleted ? 'text-rose-400' : ''}>{row.label || row.source_id.slice(0, 8)}</span>
      </td>
      <td className="p-2">
        {status ? <Badge className={`${STATUS_COLORS[status] || 'bg-muted text-muted-foreground'} border text-xs`}>{status}</Badge> : <span className="text-muted-foreground">-</span>}
      </td>
      <td className="p-2">{nextStep || <span className="text-muted-foreground">-</span>}</td>
      <td className="p-2">{ownerDisplay}</td>
      <td className="p-2">{due || <span className="text-muted-foreground">-</span>}</td>
      {!readOnly && (
        <td className="p-2 text-right">
          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); del(); }} data-testid={`delete-row-${row.id}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </td>
      )}
    </tr>
  );
}

function ActionItemRow({ item, users, readOnly, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ action: item.action, owner_id: item.owner_id || '', due_date: item.due_date || '' });
  const [busy, setBusy] = useState(false);

  const save = async () => {
    try {
      await api.put(`/pipeline-board/action-items/${item.id}`, {
        action: form.action,
        owner_id: form.owner_id || null,
        due_date: form.due_date || null,
      });
      setEditing(false);
      onUpdate();
    } catch { toast.error('Save failed'); }
  };
  const del = async () => {
    const hasTodo = !!item.linked_todo_id;
    const msg = hasTodo
      ? 'Delete this action item? The linked To-Do will also be removed from the assignee\'s tasks page.'
      : 'Delete this action item?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/pipeline-board/action-items/${item.id}`);
      onUpdate();
    } catch { toast.error('Delete failed'); }
  };
  const toggleComplete = async (checked) => {
    setBusy(true);
    try {
      await api.put(`/pipeline-board/action-items/${item.id}`, { is_completed: !!checked });
      onUpdate();
    } catch {
      toast.error('Update failed');
    } finally {
      setBusy(false);
    }
  };

  const isCompleted = !!item.is_completed;

  if (editing && !readOnly) {
    return (
      <tr className="border-b border-border bg-muted/10">
        <td className="p-2"></td>
        <td className="p-2"><Input value={form.action} onChange={e => setForm({ ...form, action: e.target.value })} className="bg-background border-border text-xs h-8" /></td>
        <td className="p-2">
          <Select value={form.owner_id || 'none'} onValueChange={v => setForm({ ...form, owner_id: v === 'none' ? '' : v })}>
            <SelectTrigger className="bg-background border-border text-xs h-8"><SelectValue placeholder="-" /></SelectTrigger>
            <SelectContent className="bg-card border-border max-h-60">
              <SelectItem value="none" className="text-xs">Unassigned</SelectItem>
              {users.filter(u => u.role !== 'Client').map(u => <SelectItem key={u.id} value={u.id} className="text-xs">{u.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </td>
        <td className="p-2"><Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-background border-border text-xs h-8" /></td>
        <td className="p-2 flex gap-0.5">
          <Button size="icon" variant="ghost" className="h-6 w-6 text-emerald-500" onClick={save}><Save className="h-3 w-3" /></Button>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground" onClick={() => setEditing(false)}>✕</Button>
        </td>
      </tr>
    );
  }

  return (
    <tr className={`border-b border-border hover:bg-muted/10 ${isCompleted ? 'opacity-60' : ''}`} data-testid={`action-item-${item.id}`}>
      <td className="p-2 align-middle">
        {!readOnly ? (
          <Checkbox
            checked={isCompleted}
            disabled={busy}
            onCheckedChange={toggleComplete}
            data-testid={`action-item-complete-${item.id}`}
          />
        ) : (
          isCompleted && <Check className="h-3 w-3 text-emerald-400" />
        )}
      </td>
      <td className={`p-2 cursor-pointer ${isCompleted ? 'line-through' : ''}`} onClick={() => !readOnly && setEditing(true)}>
        <span>{item.action}</span>
        {item.linked_todo_id && (
          <Badge variant="outline" className="ml-2 text-xs border-blue-500/40 text-blue-400" data-testid={`action-item-todo-badge-${item.id}`}>
            <ListTodo className="h-2.5 w-2.5 mr-0.5" /> To-Do
          </Badge>
        )}
      </td>
      <td className="p-2 cursor-pointer" onClick={() => !readOnly && setEditing(true)}>{item.owner_name || <span className="text-muted-foreground">-</span>}</td>
      <td className="p-2 cursor-pointer" onClick={() => !readOnly && setEditing(true)}>{item.due_date || <span className="text-muted-foreground">-</span>}</td>
      {!readOnly && (
        <td className="p-2 text-right">
          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={(e) => { e.stopPropagation(); del(); }}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </td>
      )}
    </tr>
  );
}

function RecipientManager({ recipients, onSaved }) {
  const [list, setList] = useState(recipients || []);
  const [employees, setEmployees] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    api.get('/users')
      .then(r => {
        // Active, non-Client users with an email - filter at render time.
        setEmployees((r.data || []).filter(u => u.is_active !== false && u.role !== 'Client' && u.email));
      })
      .catch(() => setEmployees([]));
  }, []);

  const addByUser = (u) => {
    if (!u || !u.email) return;
    if (list.some(r => (r.email || '').toLowerCase() === u.email.toLowerCase())) {
      toast.error('Already added');
      return;
    }
    setList([...list, { id: u.id, name: u.name || '', email: u.email }]);
    setPickerOpen(false);
  };
  const remove = (idx) => setList(list.filter((_, i) => i !== idx));
  const save = async () => {
    try {
      await api.put('/pipeline-board/recipients', { recipients: list });
      toast.success('Recipients saved');
      if (onSaved) onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const availableEmployees = employees.filter(
    u => !list.some(r => (r.email || '').toLowerCase() === (u.email || '').toLowerCase())
  );

  return (
    <div className="space-y-2" data-testid="pipeline-recipients-manager">
      <p className="text-xs font-semibold text-muted-foreground">Digest recipients</p>
      {list.length === 0 ? (
        <p className="text-xs text-muted-foreground">No recipients yet.</p>
      ) : list.map((r, i) => (
        <div key={i} className="flex items-center gap-2 text-xs">
          <div className="flex-1 min-w-0 truncate">
            <span className="font-medium">{r.name || r.email}</span>
            {r.name && <span className="text-muted-foreground"> · {r.email}</span>}
          </div>
          <Button size="icon" variant="ghost" className="h-6 w-6 text-muted-foreground hover:text-destructive" onClick={() => remove(i)} data-testid={`pipeline-recipient-remove-${i}`}>
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      ))}
      <div className="pt-2 border-t border-border space-y-2">
        {pickerOpen ? (
          <div className="bg-background border border-border rounded-md max-h-60 overflow-y-auto" data-testid="pipeline-recipients-employee-picker">
            {availableEmployees.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-4">All employees with an email are already added.</p>
            ) : (
              availableEmployees.map(u => (
                <button
                  key={u.id}
                  onClick={() => addByUser(u)}
                  className="w-full text-left px-3 py-2 hover:bg-muted/30 border-b border-border last:border-b-0 text-xs flex items-center justify-between"
                  data-testid={`pipeline-recipient-pick-${u.id}`}
                >
                  <div className="min-w-0 flex-1 truncate">
                    <span className="font-medium">{u.name}</span>
                    <span className="text-muted-foreground"> · {u.email}</span>
                  </div>
                  {u.role && <Badge variant="outline" className="text-xs shrink-0 ml-2">{u.role}</Badge>}
                </button>
              ))
            )}
          </div>
        ) : null}
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="text-xs h-7 flex-1 border-border" onClick={() => setPickerOpen(p => !p)} data-testid="pipeline-recipient-add-btn">
            <Plus className="h-3 w-3 mr-1" /> {pickerOpen ? 'Close picker' : 'Add Employee'}
          </Button>
          <Button size="sm" className="text-xs h-7 flex-1 bg-primary text-primary-foreground" onClick={save} data-testid="pipeline-recipients-save-btn">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function AddSourceModal({ open, onOpenChange, onAdded, existingRows = [] }) {
  const [sourceType, setSourceType] = useState('lead');
  const [leads, setLeads] = useState([]);
  const [rfps, setRfps] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    api.get('/leads').then(r => setLeads(r.data)).catch(() => {});
    api.get('/rfp-opportunities').then(r => setRfps(r.data)).catch(() => {});
  }, [open]);

  // Most recently added first (created_at desc). Falls back to insertion order.
  const sortByCreatedDesc = (arr) => [...arr].sort((a, b) => {
    const ad = a.created_at || '';
    const bd = b.created_at || '';
    return bd.localeCompare(ad);
  });

  // Build a set of source_ids already on the current pipeline board so we
  // can filter them out of the picker. Defends against legacy rows where
  // source_type may be missing by keying on `${type}:${id}`.
  const existingLeadIds = new Set(
    (existingRows || []).filter(r => r?.source_type === 'lead').map(r => r.source_id).filter(Boolean)
  );
  const existingRfpIds = new Set(
    (existingRows || []).filter(r => r?.source_type === 'rfp').map(r => r.source_id).filter(Boolean)
  );

  const filtered = sourceType === 'lead'
    ? sortByCreatedDesc(leads)
        .filter(l => !existingLeadIds.has(l.id))
        .filter(l => {
          const txt = `${l.contact_name || ''} ${l.company || ''} ${l.email || ''} ${l.source || ''} ${l.owner_name || ''} ${l.pipeline_stage || ''}`.toLowerCase();
          return !search || txt.includes(search.toLowerCase());
        })
    : sortByCreatedDesc(rfps)
        .filter(r => r.success_status !== 'Not Awarded')
        .filter(r => !existingRfpIds.has(r.id))
        .filter(r => {
          const txt = `${r.agency || ''} ${r.bid_title || ''} ${r.success_status || ''}`.toLowerCase();
          return !search || txt.includes(search.toLowerCase());
        });

  const add = async (id) => {
    try {
      await api.post('/pipeline-board/pipeline-rows', { source_type: sourceType, source_id: id });
      toast.success('Added to board');
      onAdded();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>Add to Pipeline Board</DialogTitle></DialogHeader>
        <div className="flex items-center gap-2">
          <div className="flex bg-card border border-border rounded-md p-0.5">
            <button
              className={`px-3 py-1 text-xs rounded ${sourceType === 'lead' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setSourceType('lead')}
              data-testid="add-source-tab-lead"
            >Leads</button>
            <button
              className={`px-3 py-1 text-xs rounded ${sourceType === 'rfp' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
              onClick={() => setSourceType('rfp')}
              data-testid="add-source-tab-rfp"
            >RFPs</button>
          </div>
          <Input placeholder={sourceType === 'lead' ? 'Search company, contact, owner…' : 'Search agency, bid title…'} value={search} onChange={e => setSearch(e.target.value)} className="bg-background border-border text-xs h-8" data-testid="add-source-search" />
        </div>
        <p className="text-xs text-muted-foreground -mt-1">Sorted by most recently added.</p>
        <div className="max-h-80 overflow-y-auto space-y-1" data-testid="add-source-list">
          {filtered.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-6">No matches.</p>
          ) : filtered.map(item => {
            const dateStr = (item.created_at || '').slice(0, 10);
            if (sourceType === 'lead') {
              const company = item.company || '';
              const contact = item.contact_name || '';
              const primary = company || contact || 'Unnamed lead';
              const secondary = company && contact ? contact : '';
              const meta = [item.source, item.owner_name && `Owner: ${item.owner_name}`, item.due_date && `Due: ${item.due_date}`].filter(Boolean).join(' · ');
              return (
                <button
                  key={item.id}
                  className="w-full text-left px-3 py-2 rounded hover:bg-muted/30 border border-transparent hover:border-border text-xs flex items-start justify-between gap-2"
                  onClick={() => add(item.id)}
                  data-testid={`add-source-row-${item.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-1.5">
                      <span className="font-medium truncate">{primary}</span>
                      {secondary && <span className="text-muted-foreground truncate">- {secondary}</span>}
                    </div>
                    {meta && <p className="text-xs text-muted-foreground mt-0.5 truncate">{meta}</p>}
                  </div>
                  <div className="flex flex-col items-end gap-0.5 shrink-0">
                    <Badge variant="outline" className="text-xs">{item.pipeline_stage || '-'}</Badge>
                    {dateStr && <span className="text-xs text-muted-foreground">added {dateStr}</span>}
                  </div>
                </button>
              );
            }
            // RFP row
            const agency = item.agency || '';
            const bid = item.bid_title || '';
            const meta = [item.due_date && `Due: ${item.due_date}`, item.contact_person].filter(Boolean).join(' · ');
            const notesPreview = (() => {
              const raw = (item.overview_notes || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
              if (!raw) return '';
              return raw.length > 280 ? raw.slice(0, 280) + '…' : raw;
            })();
            return (
              <button
                key={item.id}
                title={notesPreview ? `Notes / Comments:\n${notesPreview}` : undefined}
                className="w-full text-left px-3 py-2 rounded hover:bg-muted/30 border border-transparent hover:border-border text-xs flex items-start justify-between gap-2"
                onClick={() => add(item.id)}
                data-testid={`add-source-row-${item.id}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-medium truncate">{agency || 'Unnamed agency'}</span>
                    {bid && <span className="text-muted-foreground truncate">· {bid}</span>}
                    {notesPreview && <span className="text-xs text-amber-400 shrink-0" aria-hidden>•</span>}
                  </div>
                  {meta && <p className="text-xs text-muted-foreground mt-0.5 truncate">{meta}</p>}
                </div>
                <div className="flex flex-col items-end gap-0.5 shrink-0">
                  <Badge variant="outline" className="text-xs">{item.success_status || '-'}</Badge>
                  {dateStr && <span className="text-xs text-muted-foreground">added {dateStr}</span>}
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
